import { ColumnDef } from "@tanstack/react-table"
import { DataTableColumnHeader } from "@components/common/data-table/data-table-column-header"
import {
  CornerDownLeft,
  CornerDownRight,
  GripVertical,
  LoaderCircle,
  LocateFixedIcon,
  MessageCircleCode,
  Pin,
} from "lucide-react"
import { DictType } from "@pma/types"
import TaskEditor from "./task-editor"
import { Button } from "@components/common/button"
import { ChevronDown, ChevronRight } from "lucide-react"
import { cn, formatStringWithLink } from "@pma/utils/helpers"
import TaskDependencies from "./task-dependencies"
import TimeTracker from "./time-tracker"
import { useDrawer } from "@stores/drawer-store"
import TaskStatusEditor from "./task-status-editor"
import AssignToUser from "./assign-to-user"
import DateEditor from "./date-editor"
import SubTaskMenu from "./sub-task-menu"
import useTranslator from "@hooks/use-translator"
import AttachFileToTask from "./task-attached-files"
import { useAuth } from "@pma/stores/auth-store"
import TaskPriorityEditor from "./task-priority-editor"
import { Link } from "react-router-dom"
import { Tooltip, TooltipContent, TooltipTrigger } from "../common/tooltip"
import ExpandTask from "./expand-task"
import TableCheckbox from "@components/common/data-table/checkbox"
import { GroupedTasksContext } from "./grouped-tasks/context/GroupedTasksProvider"
import React from "react"

export const tasksColumns: ColumnDef<any>[] = [
  {
    id: "drag-handle",
    header: () => <div className='w-[10px]'></div>,
    cell: ({ row }) => (
      <div className='flex w-[30px] items-center justify-center'>
        <GripVertical size={14} className='cursor-grab focus:outline-none focus:ring-0' />
      </div>
    ),
    enableSorting: false,
    size: 30,
  },
  {
    id: "select-col",
    header: ({ table }) => (
      <TableCheckbox
        checked={table.getIsAllRowsSelected()}
        onChange={table.getToggleAllRowsSelectedHandler()} //or getToggleAllPageRowsSelectedHandler
      />
    ),
    cell: ({ row }) => {
      return (
        <TableCheckbox checked={row.getIsSelected()} onChange={row.getToggleSelectedHandler()} />
      )
    },
  },
  {
    id: "expander",
    header: () => null,
    cell: ({ row }) => {
      const { setTaskExpandForLoading, search, groupedTasksLoading } =
        React.useContext(GroupedTasksContext)
      const hasParent = row?.original?.parent_tasks_id
      const isLoading = row?.original?.isLoadingSubTasks ?? false
      const parentId = row?.original?.id
      const { locale } = useTranslator()
      const isHebrew = locale == "he"
      const subItems = row.original?._subtasks
      const hasRunSearchRef = React.useRef(false)

      const hasMatchingSubtask = React.useMemo(() => {
        if (!search || !subItems || subItems.length === 0) return false
        const searchLower = search.toLowerCase()
        return subItems.some((subtask: any) => {
          const taskIdFormatted = `PR-${subtask.id}`.toLowerCase()
          return (
            taskIdFormatted.includes(searchLower) ||
            subtask.title?.toLowerCase().includes(searchLower) ||
            subtask.description?.toLowerCase().includes(searchLower)
          )
        })
      }, [search, subItems])

      React.useEffect(() => {
        if (
          search &&
          !hasRunSearchRef.current &&
          !hasParent &&
          subItems?.length > 0 &&
          !isLoading &&
          !row.getIsExpanded() &&
          !groupedTasksLoading &&
          hasMatchingSubtask
        ) {
          setTaskExpandForLoading(parentId, row)
          hasRunSearchRef.current = true
        }
      }, [
        search,
        hasMatchingSubtask,
        hasParent,
        subItems,
        isLoading,
        row,
        groupedTasksLoading,
        setTaskExpandForLoading,
        parentId,
      ])

      const handleExpand = () => {
        const currentExpandStatus = row.getIsExpanded()
        if (!currentExpandStatus && parentId && subItems?.length > 0) {
          setTaskExpandForLoading(parentId, row)
        } else {
          row.toggleExpanded()
        }
      }

      if (isLoading) {
        return <LoaderCircle className={cn("animate-spin text-primary")} size={24} />
      }

      return (
        <>
          {!hasParent ? (
            <div className='relative flex w-[30px] items-center gap-1 p-0'>
              <Button
                variant={"ghost"}
                size={"icon"}
                className='cursor-pointer'
                {...{
                  onClick: handleExpand,
                }}
              >
                {row.getIsExpanded() ? (
                  <ChevronDown />
                ) : (
                  <ChevronRight className={cn(isHebrew && "rotate-180")} />
                )}
              </Button>
              {subItems?.length > 0 && !row.getIsExpanded() && (
                <small
                  className={cn(
                    "absolute left-3 top-0 flex h-3 w-3 items-center justify-center rounded-full bg-slate-300 text-[10px] text-white",
                    isHebrew ? "-left-4" : "-right-4 ml-1",
                  )}
                >
                  {subItems?.length}
                </small>
              )}
            </div>
          ) : (
            <>
              {/* Task SubItem indent icons */}
              {isHebrew ? (
                <CornerDownLeft
                  className={cn("ml-6 text-primary", isHebrew && "rotate-[deg]")}
                  size={12}
                />
              ) : (
                <CornerDownRight className={cn("ml-6 text-primary")} size={12} />
              )}
            </>
          )}
        </>
      )
    },
  },

  {
    accessorKey: "id",
    enableSorting: true,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title='Ticket'
        className='flex max-w-[80px] items-center'
      />
    ),
    cell: ({ row }) => {
      const task = row.original
      return <div className={cn("w-fit text-xs")}>{task.number}</div>
    },
  },
  {
    accessorKey: "created_by.name",
    id: "created_by.name",
    enableSorting: true,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title='Created By'
        className='flex max-w-[150px] items-center'
      />
    ),
    size: 500,
    enableResizing: true,
    cell: ({ row }: any) => {
      const task = row?.original as DictType
      const create_by = task?.created_by
      const user_name = create_by?.name
      return <div className='w-fit text-xs'>{user_name}</div>
    },
    // sortingFn: (rowA, rowB) => {
    //   const taskOneCreatorName = rowA.original.created_by?.name?.toLowerCase() || ""
    //   const taskTwoCreatorName = rowB.original.created_by?.name?.toLowerCase() || ""
    //   return taskOneCreatorName < taskTwoCreatorName
    //     ? -1
    //     : taskOneCreatorName > taskTwoCreatorName
    //       ? 1
    //       : 0
    // },
  },
  {
    accessorKey: "title",
    enableSorting: true,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title='Title'
        className='flex w-[300px] max-w-[350px] items-center'
      />
    ),
    cell: ({ row }: any) => {
      const title = row.getValue("title") as string
      const hasParent = row?.original?.parent_tasks_id
      const task = row?.original
      const { authConfig } = useAuth()
      const parentTaskId = task?.parent_tasks_id
      const userId = authConfig?.id

      const pinnedByUser = task?.pinned_by_user_id?.includes(userId)

      return (
        <div
          className={cn(
            "relative flex w-fit min-w-[180px] items-center text-left",
            hasParent && "ml-2",
          )}
        >
          <span className='absolute -right-5 -top-5 rotate-45'>
            {pinnedByUser && <Pin size={14} />}
          </span>{" "}
          <TaskEditor
            id={row.original?.id}
            title={"title"}
            value={row.original?.title}
            parentTaskId={parentTaskId}
          >
            <div className='relative'>
              <div
                className='w-fit max-w-[35ch] text-left font-normal'
                dangerouslySetInnerHTML={{
                  __html: formatStringWithLink(title),
                }}
              />
            </div>
          </TaskEditor>
        </div>
      )
    },
  },
  {
    accessorKey: "notes",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title='Notes' className='block' />
    ),
    cell: ({ row }) => {
      const drawer = useDrawer()
      const notesCount = row.original?.notes_id?.length

      return (
        <div className='flex items-center truncate text-ellipsis'>
          <div className='relative flex h-full min-h-9 w-[50px] items-center justify-center'>
            <MessageCircleCode
              size={16}
              onClick={() => {
                drawer.onOpen()
                drawer.setContext("notes")
                drawer.setTaskId(row.original?.id)
              }}
              className='cursor-pointer transition-all hover:scale-125'
            />
            {notesCount > 0 && (
              <small className='absolute right-2 top-1 z-10 flex h-3 w-3 items-center justify-center rounded-full bg-primary/80 text-[9px] text-white'>
                {notesCount}
              </small>
            )}
          </div>
        </div>
      )
    },
    enableSorting: false,
  },
  {
    accessorKey: "description",
    header: ({ column }) => <DataTableColumnHeader column={column} title='Description' />,
    cell: ({ row }) => {
      const description: string = row.getValue("description")
      const { locale } = useTranslator()
      const isHebrew = locale == "he"
      const task = row?.original
      const parentTaskId = task?.parent_tasks_id

      return (
        <div className='flex space-x-2'>
          <TaskEditor
            id={row.original?.id}
            title={"description"}
            value={description}
            parentTaskId={parentTaskId}
          >
            <div
              className={cn("text-xs font-normal", isHebrew ? "rtl text-right" : "ltr text-left")}
            >
              {description ? (
                <div
                  className='w-fit min-w-[30ch] text-left'
                  dangerouslySetInnerHTML={{
                    __html: formatStringWithLink(description),
                  }}
                />
              ) : (
                "---"
              )}
            </div>
          </TaskEditor>
        </div>
      )
    },
    enableSorting: true,
  },
  {
    accessorFn: (row: DictType) => row?.status?.name,
    id: "status.name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title='Status' className='hidden md:block' />
    ),
    cell: ({ row }) => {
      const task = row?.original as DictType
      const status = task?.status
      const priority_id = task?.priority_id
      const parentTaskId = task?.parent_tasks_id

      return (
        <div className='hidden max-w-[15ch] flex-col justify-start text-wrap md:flex'>
          <TaskStatusEditor
            status={status}
            taskId={row?.original?.id}
            priorityId={priority_id}
            parentTaskId={parentTaskId}
          />
        </div>
      )
    },
    enableSorting: true,
    sortUndefined: "last",
  },
  {
    accessorFn: (row: DictType) => row?.priority_id,
    id: "_priority.level",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title='Priority' className='hidden md:block' />
    ),
    cell: ({ row }) => {
      const task = row?.original as DictType
      const priority = task?._priority
      const parentTaskId = task?.parent_tasks_id

      return (
        <div className='hidden max-w-[15ch] flex-col justify-start text-wrap md:flex'>
          <TaskPriorityEditor
            key={row?.original?.id}
            priority={priority}
            taskId={row?.original?.id}
            parentTaskId={parentTaskId}
          />
        </div>
      )
    },
    enableSorting: true,
    sortUndefined: "last",
  },
  {
    accessorKey: "responsible",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title='Assigned' className='hidden w-40 md:block' />
    ),
    cell: ({ row }: DictType) => {
      const task = row.original
      const parentTaskId = task?.parent_tasks_id
      return (
        <small className='hidden w-40 flex-col justify-start text-wrap md:flex'>
          <AssignToUser
            key={task?.id}
            taskId={task?.id}
            responsible={task?.responsible_id}
            parentTaskId={parentTaskId}
            tableView={true}
          />
        </small>
      )
    },
    enableSorting: false,
  },
  {
    accessorKey: "_gis",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title='Gis' className='hidden md:block' />
    ),
    cell: ({ row }: DictType) => {
      const { translate } = useTranslator()
      const task = row.original
      const gis = task?._gis

      return (
        <div className='hidden max-w-[15ch] flex-col justify-start text-wrap md:flex'>
          {gis?.data ? (
            <Tooltip>
              <TooltipTrigger>
                <Link to={`?lng=${gis?.data?.longitude}&lat=${gis?.data?.latitude}`}>
                  <LocateFixedIcon />
                </Link>
              </TooltipTrigger>
              <TooltipContent>{translate("View on map")}</TooltipContent>
            </Tooltip>
          ) : (
            "--"
          )}
        </div>
      )
    },
    enableSorting: false,
  },
  {
    accessorKey: "files",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title='Files' className='hidden md:block' />
    ),
    cell: ({ row }: DictType) => {
      const task = row.original
      const files = task?.linked_files_id
      const parentTaskId = task?.parent_tasks_id

      return (
        <div className='hidden max-w-[15ch] flex-col justify-start text-wrap md:flex'>
          <AttachFileToTask taskId={task?.id} files={files} parentTaskId={parentTaskId} />
        </div>
      )
    },
    enableSorting: false,
  },
  {
    accessorKey: "Duration",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title='Duration' className='hidden md:block' />
    ),
    cell: ({ row }: DictType) => {
      const task = row.original
      const parentTaskId = task?.parent_tasks_id
      return (
        <DateEditor
          value={{
            from: row.original?.start_at,
            to: row.original?.finish_to,
          }}
          taskId={row?.original?.id}
          parentTaskId={parentTaskId}
        />
      )
    },
    enableSorting: false,
  },
  {
    accessorKey: "Dependencies",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title='Dependencies' className='hidden md:block' />
    ),
    cell: ({ row, table }) => {
      const task = row.original
      const parentTaskId = task?.parent_tasks_id
      return (
        <div className='hidden items-center truncate text-ellipsis md:flex'>
          <TaskDependencies
            currentTask={row.original}
            tasks={table?.options?.data}
            parentTaskId={parentTaskId}
          />
        </div>
      )
    },
    enableSorting: false,
  },
  {
    accessorKey: "sessions",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title='Time' className='hidden md:block' />
    ),
    cell: ({ row }) => {
      const task = row.original
      const parentTaskId = task?.parent_tasks_id
      return (
        <div className='hidden items-center gap-2 truncate text-ellipsis md:flex'>
          <TimeTracker task={row.original} parentTaskId={parentTaskId} />
        </div>
      )
    },
    enableSorting: false,
  },
]
